import React, { useRef } from 'react';

import * as NavigationBar from 'expo-navigation-bar';
import * as SplashScreen from 'expo-splash-screen';
import * as StatusBar from 'expo-status-bar';
import { VideoView, useVideoPlayer } from 'expo-video';
import LottieView from 'lottie-react-native';
import { Image, StyleSheet, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context';

import { useStartupSplashController } from 'hooks/use-startup-splash-controller';
import { isAndroid } from 'utils/environment';

import { AppInner } from './AppInner';

export { LOTTIE_SPLASH_SCREEN_DURATION_MS, WIGGLE_ROOM } from 'hooks/use-startup-splash-controller';

if (isAndroid) {
  // normalize android to work like iOS as much as possible to minimize visual inconsistencies
  (async () => {
    try {
      await NavigationBar.setBackgroundColorAsync('transparent');
      await NavigationBar.setBehaviorAsync('overlay-swipe');
    } catch {}
  })();
  StatusBar.setStatusBarTranslucent(true);
  StatusBar.setStatusBarBackgroundColor('transparent');
  SplashScreen.setOptions({
    fade: false,
  });
}

const splashLogoPng = require('./assets/images/splash-logo.png');
const SPLASH_BACKGROUND_COLOR = '#F8F1E7';

export const App: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    animatedSplashVisible,
    handleAppReadyToHideSplash,
    handleSplashAnimationComplete,
    handleSplashVideoFallback,
    shouldShowSplashVideo,
    shouldShowStaticStartupSplash,
    splashVideoSource,
    visibleSplashAnimationSource,
  } = useStartupSplashController();

  return (
    <GestureHandlerRootView style={styles.gestureRoot}>
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
        <AppInner routes={children} onReadyToHideSplash={handleAppReadyToHideSplash} />
        {animatedSplashVisible &&
          (shouldShowSplashVideo ? (
            <SplashVideo
              video={splashVideoSource}
              onComplete={handleSplashAnimationComplete}
              onPlaybackError={handleSplashVideoFallback}
            />
          ) : shouldShowStaticStartupSplash ? (
            <StaticStartupSplash />
          ) : (
            <LottieView
              resizeMode="cover"
              hardwareAccelerationAndroid
              autoPlay
              source={visibleSplashAnimationSource}
              style={styles.lottieView}
              loop={false}
              onAnimationFinish={handleSplashAnimationComplete}
              onAnimationFailure={handleSplashAnimationComplete}
            />
          ))}
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
};

const StaticStartupSplash = () => (
  <View style={styles.staticSplash} testID="static-startup-splash">
    <Image resizeMode="contain" source={splashLogoPng} style={styles.staticSplashLogo} />
  </View>
);

// separate component to avoid any native video stuff unless its active due to API design
const SplashVideo = ({
  video,
  onComplete,
  onPlaybackError,
}: {
  video: any;
  onComplete: () => void;
  onPlaybackError: () => void;
}) => {
  const hasCompletedRef = useRef(false);

  const markComplete = () => {
    if (hasCompletedRef.current) {
      return;
    }
    hasCompletedRef.current = true;
    onComplete();
  };

  const player = useVideoPlayer(video, player => {
    player.loop = false;
    player.muted = true;
    player.audioMixingMode = 'mixWithOthers';
    player.addListener('playToEnd', markComplete);
    player.addListener('statusChange', payload => {
      if (payload.error) {
        onPlaybackError();
        return;
      }

      if (payload.status === 'idle' && payload.oldStatus === 'readyToPlay') {
        markComplete();
      }
    });
    player.play();
  });
  return (
    <VideoView
      style={styles.splashVideo}
      contentFit="cover"
      nativeControls={false}
      allowsPictureInPicture={false}
      // @ts-ignore - VideoPlayer type mismatch between native and web
      player={player}
    />
  );
};

const styles = StyleSheet.create({
  gestureRoot: {
    flex: 1,
  },
  lottieView: {
    position: 'absolute',
    alignItems: 'center',
    backgroundColor: SPLASH_BACKGROUND_COLOR,
    justifyContent: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  splashVideo: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: SPLASH_BACKGROUND_COLOR,
  },
  staticSplash: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    backgroundColor: SPLASH_BACKGROUND_COLOR,
    justifyContent: 'center',
  },
  staticSplashLogo: {
    height: 169,
    width: 152,
  },
});
